import type { QueryClient } from '@tanstack/react-query';
import { Appointment, appointmentHasTag, appointmentKey, AppointmentStatus, Employee, INTERNAL_APPOINTMENT_TAG } from '../types';
import { dayjs } from './dayjs-config';
import { WEBSITE_ID } from '../projectConfig';
import { hydrateAppointmentsIntoCache } from './cache-delegate';

/**
 * Orchestrator-mode utility surface.
 *
 * Why this file exists:
 * - Orchestrator mode adds polling, delta detection, audible alerts, and notification feed behavior.
 * - Those concerns are intentionally grouped here so AdminDataContext stays readable and non-orchestrator
 *   data-fetch logic remains separate.
 */
export type OrchestratorBookingNotification = {
  id: string;
  appointment: Appointment;
  detectedAtMs: number;
  read: boolean;
};

export const ORCHESTRATOR_REFRESH_INTERVAL_MS = 1000 * 60 * 10;
const APPOINTMENT_LOOKBACK_WEEKS = 4;
export const MAX_BOOKING_NOTIFICATIONS = 50;
const ORCHESTRATOR_CHIME_AUDIO_URL = `${process.env.PUBLIC_URL}/sounds/orchestrator-booking-chime2.mp3`;

let orchestratorChimeAudioTemplate: HTMLAudioElement | null = null;

export const collectRecentAppointmentIdsFromEmployees = (employees: Employee[]): string[] => {
  const minimumDate = dayjs.tz().startOf('day').subtract(APPOINTMENT_LOOKBACK_WEEKS, 'week');
  const ids = new Set<string>();

  employees.forEach((employee) => {
    Object.entries(employee.appointments ?? {}).forEach(([dateKey, dateIds]) => {
      if (!Array.isArray(dateIds)) {
        return;
      }
      const appointmentDate = dayjs(dateKey, 'MMDDYYYY');
      if (!appointmentDate.isValid() || appointmentDate.isBefore(minimumDate)) {
        return;
      }
      dateIds.forEach((id) => {
        if (typeof id === 'string' && id.trim().length > 0) {
          ids.add(id);
        }
      });
    });
  });

  return Array.from(ids).sort();
};

const addAppointmentIdToDateIndex = (
  appointmentsMap: Record<string, string[]> | undefined,
  dateKey: string,
  appointmentId: string
): Record<string, string[]> => {
  const next = { ...(appointmentsMap ?? {}) };
  const existing = next[dateKey] ?? [];
  if (!existing.includes(appointmentId)) {
    next[dateKey] = [...existing, appointmentId];
  }
  return next;
};

const removeAppointmentIdFromDateIndex = (
  appointmentsMap: Record<string, string[]> | undefined,
  appointmentId: string
): Record<string, string[]> => {
  const next: Record<string, string[]> = {};
  Object.entries(appointmentsMap ?? {}).forEach(([dateKey, ids]) => {
    const filteredIds = ids.filter((id) => id !== appointmentId);
    if (filteredIds.length > 0) {
      next[dateKey] = filteredIds;
    }
  });
  return next;
};

const mergeAppointmentIntoCachedList = (
  cached: Appointment[] | undefined,
  appointment: Appointment
): Appointment[] | undefined => {
  if (!cached) return cached;
  const idx = cached.findIndex((entry) => entry.id === appointment.id);
  if (idx === -1) {
    return [...cached, appointment];
  }
  const next = [...cached];
  next[idx] = appointment;
  return next;
};

export const setupOrchestratorChimeUnlock = (
  onUnlock: () => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => { };
  }

  let unlocked = false;
  const unlockPlayback = () => {
    if (unlocked) {
      return;
    }
    unlocked = true;
    onUnlock();
    console.log('[Orchestrator Chime] Playback unlocked by user interaction.');
  };

  window.addEventListener('pointerdown', unlockPlayback, { passive: true });
  window.addEventListener('keydown', unlockPlayback, { passive: true });
  window.addEventListener('touchstart', unlockPlayback, { passive: true });

  return () => {
    window.removeEventListener('pointerdown', unlockPlayback);
    window.removeEventListener('keydown', unlockPlayback);
    window.removeEventListener('touchstart', unlockPlayback);
  };
};

export const playNewBookingChime = async (allowPlayback: boolean) => {
  if (typeof window === 'undefined') {
    console.log('[Orchestrator Chime] Skipped playback because window is unavailable.');
    return;
  }

  if (!allowPlayback) {
    console.log('[Orchestrator Chime] Skipped playback because this tab has not received user interaction yet.');
    return;
  }

  if (ORCHESTRATOR_CHIME_AUDIO_URL.length > 0) {
    try {
      if (!orchestratorChimeAudioTemplate) {
        orchestratorChimeAudioTemplate = new Audio(ORCHESTRATOR_CHIME_AUDIO_URL);
        orchestratorChimeAudioTemplate.preload = 'auto';
      }
      const audio = orchestratorChimeAudioTemplate.cloneNode(true) as HTMLAudioElement;
      audio.currentTime = 0;
      await audio.play();
      console.log(`[Orchestrator Chime] Played custom audio from ${ORCHESTRATOR_CHIME_AUDIO_URL}`);
      return;
    } catch (error) {
      console.error('[Orchestrator Chime] Failed to play custom audio URL. Falling back to generated tone.', error);
    }
  } else {
    console.log('[Orchestrator Chime] No custom audio URL configured. Using generated tone fallback.');
  }

  const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) {
    console.warn('[Orchestrator Chime] AudioContext is unavailable, cannot play fallback tone.');
    return;
  }

  const audioContext = new AudioContextConstructor();
  try {
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.exponentialRampToValueAtTime(1320, now + 0.18);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.36);
    console.log('[Orchestrator Chime] Played generated fallback tone.');
  } catch (error) {
    console.warn('[Orchestrator Chime] Unable to play fallback tone:', error);
  } finally {
    window.setTimeout(() => {
      void audioContext.close();
    }, 400);
  }
};

export const findMissingOrchestratorAppointmentIds = (
  employees: Employee[],
  queryClient: QueryClient,
  siteId: string = WEBSITE_ID
) => {
  const latestAppointmentIds = collectRecentAppointmentIdsFromEmployees(employees);
  const missingAppointmentIds = latestAppointmentIds.filter(
    (appointmentId) => queryClient.getQueryData(appointmentKey(siteId, appointmentId)) == null
  );

  return { latestAppointmentIds, missingAppointmentIds };
};

export const hydrateMissingOrchestratorAppointments = async (
  queryClient: QueryClient,
  missingAppointmentIds: string[],
  siteId: string = WEBSITE_ID
) => {
  const { hydrated } = await hydrateAppointmentsIntoCache(queryClient, missingAppointmentIds, siteId);
  const hydratedAppointments = missingAppointmentIds
    .map((id) => queryClient.getQueryData(appointmentKey(siteId, id)) as Appointment | undefined)
    .filter((appointment): appointment is Appointment => appointment !== undefined);

  return { hydrated, hydratedAppointments };
};

export const getCachedAppointmentsByIds = (
  queryClient: QueryClient,
  appointmentIds: string[],
  siteId: string = WEBSITE_ID
): Appointment[] => (
  appointmentIds
    .map((id) => queryClient.getQueryData(appointmentKey(siteId, id)) as Appointment | undefined)
    .filter((appointment): appointment is Appointment => appointment !== undefined)
);

export const filterNotifiableOrchestratorAppointments = (
  appointments: Appointment[]
): Appointment[] => (
  appointments.filter((appointment) => (
    appointment.status !== AppointmentStatus.INTERNAL_BOOKING
    && appointment.status !== AppointmentStatus.NON_APPOINTMENT
    && !appointmentHasTag(appointment, INTERNAL_APPOINTMENT_TAG)
  ))
);

export const prependOrchestratorNotifications = (
  previous: OrchestratorBookingNotification[],
  appointments: Appointment[],
  detectedAtMs: number,
  maxItems: number = MAX_BOOKING_NOTIFICATIONS
): OrchestratorBookingNotification[] => {
  const additions: OrchestratorBookingNotification[] = appointments.map((appointment) => ({
    id: `${appointment.id}:${detectedAtMs}`,
    appointment,
    detectedAtMs,
    read: false,
  }));
  return [...additions, ...previous].slice(0, maxItems);
};

export const reconcileAppointmentIntoAdminCaches = (
  queryClient: QueryClient,
  refreshedAppointment: Appointment,
  siteId: string = WEBSITE_ID
) => {
  queryClient.setQueryData<Employee[] | undefined>(['employees'], (previous) => (
    previous?.map((employee) => (
      ({
        ...employee,
        appointments: (
          employee.id === refreshedAppointment.employeeID
            ? addAppointmentIdToDateIndex(
              removeAppointmentIdFromDateIndex(employee.appointments, refreshedAppointment.id),
              refreshedAppointment.appointmentDate,
              refreshedAppointment.id
            )
            : removeAppointmentIdFromDateIndex(employee.appointments, refreshedAppointment.id)
        ),
      })
    ))
  ));
  queryClient.setQueryData<Employee[] | undefined>(['allEmployees'], (previous) => (
    previous?.map((employee) => (
      ({
        ...employee,
        appointments: (
          employee.id === refreshedAppointment.employeeID
            ? addAppointmentIdToDateIndex(
              removeAppointmentIdFromDateIndex(employee.appointments, refreshedAppointment.id),
              refreshedAppointment.appointmentDate,
              refreshedAppointment.id
            )
            : removeAppointmentIdFromDateIndex(employee.appointments, refreshedAppointment.id)
        ),
      })
    ))
  ));
  queryClient.setQueryData<Employee | undefined>(['currentEmployee'], (previous) => {
    if (!previous) {
      return previous;
    }
    const withoutAppointmentId = removeAppointmentIdFromDateIndex(previous.appointments, refreshedAppointment.id);
    if (previous.id !== refreshedAppointment.employeeID) {
      return {
        ...previous,
        appointments: withoutAppointmentId,
      };
    }
    return {
      ...previous,
      appointments: addAppointmentIdToDateIndex(
        withoutAppointmentId,
        refreshedAppointment.appointmentDate,
        refreshedAppointment.id
      ),
    };
  });

  // Remove stale copies from every employee-scoped list cache first (handoff/move safety),
  // then upsert into the destination employee caches below.
  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['appointmentsByEmployee'] },
    (previous) => previous?.filter((appointment) => appointment.id !== refreshedAppointment.id)
  );
  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['employeeCalendarAppointments', siteId] },
    (previous) => previous?.filter((appointment) => appointment.id !== refreshedAppointment.id)
  );
  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['teamDayAppointments', siteId] },
    (previous) => previous?.filter((appointment) => appointment.id !== refreshedAppointment.id)
  );

  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['appointmentsByEmployee', refreshedAppointment.employeeID] },
    (previous) => mergeAppointmentIntoCachedList(previous, refreshedAppointment)
  );
  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['employeeCalendarAppointments', siteId, refreshedAppointment.employeeID] },
    (previous) => mergeAppointmentIntoCachedList(previous, refreshedAppointment)
  );
  queryClient.setQueriesData<Appointment[] | undefined>(
    { queryKey: ['teamDayAppointments', siteId, refreshedAppointment.appointmentDate] },
    (previous) => mergeAppointmentIntoCachedList(previous, refreshedAppointment)
  );
};
